import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './ProductDetailPage.css';

// Detailed mapping of all products
const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [dbProduct, setDbProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const enableLocalApi = process.env.REACT_APP_ENABLE_LOCAL_API === 'true';
  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
  const API_URL = envApiUrl || '/api';
  const useStaticCatalogOnly = process.env.NODE_ENV === 'development'
    && isLocalHost
    && !!envApiUrl
    && envApiUrl.includes('localhost')
    && !enableLocalApi;
  const fetchConfig = useMemo(() => ({
    apiUrl: API_URL,
    useStaticCatalogOnly,
    slug
  }), [API_URL, useStaticCatalogOnly, slug]);

  useEffect(() => {
    const fetchProduct = async () => {
      if (fetchConfig.useStaticCatalogOnly) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await fetch(`${fetchConfig.apiUrl}/products/${fetchConfig.slug}`, {
          headers: {
          }
        });
        const json = await res.json();
        if (json.success && json.data) {
          setDbProduct(json.data);
        }
      } catch (err) {
        console.error('Error fetching product details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [fetchConfig]);
  // Resolve images
const NO_IMAGE_PLACEHOLDER = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSczMDAnIGhlaWdodD0nMzAwJyB2aWV3Qm94PScwIDAgMzAwIDMwMCc+PHJlY3Qgd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgZmlsbD0nI2YxZjVmOScvPjx0ZXh0IHg9JzUwJScgeT0nNTAlJyBkb21pbmFudC1iYXNlbGluZT0nbWlkZGxlJyB0ZXh0LWFuY2hvcj0nbWlkZGxlJyBmb250LWZhbWlseT0nc2Fucy1zZXJpZicgZm9udC1zaXplPScxNicgZmlsbD0nIzk0YTNiOCc+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+";

  const getImage = useCallback((imgName) => {
    if (!imgName) return NO_IMAGE_PLACEHOLDER;
    if (imgName.startsWith('http://') || imgName.startsWith('https://') || imgName.startsWith('data:')) {
      if (imgName.includes('ik.imagekit.io')) {
        const hasParams = imgName.includes('?');
        return imgName + (hasParams ? '&' : '?') + 'tr=w-800,q-80,f-auto';
      }
      return imgName;
    }

    const cleanImgName = (imgName.startsWith('/uploads/') || imgName.startsWith('uploads/')) ? imgName : `/uploads/${imgName}`;
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('172.') || window.location.hostname.startsWith('10.') || window.location.port !== '';
    let backendBase = isLocalHost ? 'http://localhost:5000' : fetchConfig.apiUrl.replace('/api', '');
    if (!backendBase || backendBase.startsWith('/')) {
      backendBase = `${window.location.protocol}//${window.location.hostname}:5000`;
    }
    return `${backendBase}${cleanImgName.startsWith('/') ? '' : '/'}${cleanImgName}`;
  }, [fetchConfig]);

  const mediaList = useMemo(() => {
    let list = [];
    if (dbProduct) {
      if (dbProduct.images && dbProduct.images.length > 0) {
        list = [...list, ...dbProduct.images.map(img => ({ type: 'image', src: getImage(img) }))];
      }
      if (dbProduct.videoUrl) {
        list.push({ type: 'video', src: dbProduct.videoUrl });
      }
    }
    
    // If no media found from DB
    if (list.length === 0) {
      list = [
        { type: 'image', src: getImage(null) },
      ];
    }
    return list;
  }, [dbProduct, getImage]);

  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  if (loading) {
    return (
      <div className="product-detail-page">
        <Header />
        <div style={{ textAlign: 'center', padding: '100px 20px', color: '#4BA83D' }}>Loading...</div>
        <Footer />
      </div>
    );
  }

  if (!dbProduct) {
    return (
      <div className="product-detail-page">
        <Header />
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h2>{t('product_detail.not_found', 'Product Not Found')}</h2>
          <p>{t('product_detail.not_found_desc', 'The product you are looking for does not exist or has been removed.')}</p>
          <button onClick={() => navigate('/products')} style={{ marginTop: '20px', padding: '10px 20px', background: '#4BA83D', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Back to Products</button>
        </div>
        <Footer />
      </div>
    );
  }

  // Derive display product details
  const displayTitle = dbProduct.name;
  const displayDescription = dbProduct.description;

  const isYouTube = (url) => url && (url.includes('youtube.com') || url.includes('youtu.be'));

  const getYouTubeEmbedUrl = (url) => {
    let videoId = '';
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1].split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      return url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  const nextMedia = () => {
    setCurrentMediaIndex((prev) => (prev + 1) % mediaList.length);
  };

  const prevMedia = () => {
    setCurrentMediaIndex((prev) => (prev - 1 + mediaList.length) % mediaList.length);
  };
  
  // Specifications
  let displaySpecs = [];
  if (dbProduct.specifications) {
    const specsObj = dbProduct.specifications instanceof Map ? Object.fromEntries(dbProduct.specifications) : dbProduct.specifications;
    displaySpecs = Object.entries(specsObj).map(([name, value]) => ({ name, value }));
  }

  // Extra details
  const displayApplications = dbProduct.applications || t('product_detail.default_applications');
  const displayHowToUse = (dbProduct.howToUse && dbProduct.howToUse.length > 0) 
    ? dbProduct.howToUse 
    : t('product_detail.default_howToUse', { returnObjects: true });
  const displayKeyBenefits = (dbProduct.keyBenefits && dbProduct.keyBenefits.length > 0)
    ? dbProduct.keyBenefits
    : t('product_detail.default_keyBenefits', { returnObjects: true });
  const displayTechFeatures = (dbProduct.techFeatures && dbProduct.techFeatures.length > 0)
    ? dbProduct.techFeatures
    : t('product_detail.default_techFeatures', { returnObjects: true });

  return (
    <div className="product-detail-page">
      <Header />
      
      <div className="product-detail-container">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          {t('product_detail.back')}
        </button>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '5rem 0', color: 'var(--primary-color)' }}>{t('product_detail.loading')}</div>
        ) : (
          <div className="product-detail-card">
            <div className="product-header">
              <h1 className="product-title">{displayTitle}</h1>
              <span className="stock-badge">{t('product_detail.in_stock')}</span>
            </div>

            <div className="product-hero-image">
              {mediaList[currentMediaIndex].type === 'video' ? (
                isYouTube(mediaList[currentMediaIndex].src) ? (
                  <iframe 
                    src={getYouTubeEmbedUrl(mediaList[currentMediaIndex].src)} 
                    className="carousel-media" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; compute-pressure" 
                    allowFullScreen 
                    title={displayTitle}
                  />
                ) : (
                  <video src={mediaList[currentMediaIndex].src} controls className="carousel-media" />
                )
              ) : (
                <img src={mediaList[currentMediaIndex].src} alt={displayTitle} className="carousel-media" />
              )}
              
              {/* Always show arrows so user knows the functionality exists */}
              <button className="carousel-btn prev-btn" onClick={prevMedia}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <button className="carousel-btn next-btn" onClick={nextMedia}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
              <div className="carousel-indicators">
                {mediaList.map((_, idx) => (
                  <span 
                    key={idx} 
                    className={`indicator-dot ${idx === currentMediaIndex ? 'active' : ''}`}
                    onClick={() => setCurrentMediaIndex(idx)}
                  />
                ))}
              </div>
            </div>

            <div className="specs-section">
              <div className="specs-header">{t('product_detail.tech_specs')}</div>
              <table className="specs-table">
                <tbody>
                  {displaySpecs.map((spec, i) => (
                    <tr key={i}>
                      <td>{spec.name}</td>
                      <td className="spec-value">{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="about-section">
              <h2 className="section-title">{t('product_detail.about')}</h2>
              <p className="description">{displayDescription}</p>
            </div>

            <div className="applications-box">
              <h3 className="applications-title">{t('product_detail.applications')}</h3>
              <p className="applications-text">{displayApplications}</p>
            </div>

            <div className="how-to-use-section">
              <div className="how-to-header">{t('product_detail.how_to_use')}</div>
              <div className="how-to-list">
                {displayHowToUse.map((step, index) => (
                  <div key={index} className="how-to-step">
                    <div className="step-num">{index + 1}</div>
                    <p className="step-text">{index + 1}. {step}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="key-benefits-section">
              <h2 className="section-title">{t('product_detail.key_benefits')}</h2>
              <div className="benefits-list">
                {displayKeyBenefits.map((benefit, index) => (
                  <div key={index} className="benefit-item">
                    <svg className="check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ba83d" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="tech-features-section">
              <div className="tech-header">{t('product_detail.tech_features')}</div>
              <div className="tech-list">
                {displayTechFeatures.map((feature, index) => (
                  <div key={index} className="tech-item">
                    <span className="orange-dot"></span>
                    <p>{feature}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="enquiry-cta-box">
              <h3 className="cta-heading">{t('product_detail.interested')}</h3>
              <div className="cta-buttons">
                <button className="btn-wa-enquiry" onClick={() => navigate(`/enquire?product=${slug}&productName=${encodeURIComponent(displayTitle)}`)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '5px'}}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  {t('product_detail.enquire_now')}
                </button>
                <button className="btn-call-now-cta" onClick={() => window.location.href = 'tel:+91 96312 77264'}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.27-2.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                  {t('product_detail.call_now')}
                </button>
              </div>
              <p className="cta-sub">{t('product_detail.cta_sub')}</p>
            </div>
          </div>
        )}
      </div>

      <div className="whatsapp-fab-fixed" onClick={() => window.open(`https://wa.me/919631277264?text=Hello%2C%20I%20am%20interested%20in%20the%20${encodeURIComponent(displayTitle)}`, '_blank')} style={{ cursor: 'pointer' }}>
        <svg viewBox="0 0 24 24" width="30" height="30" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.438 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .004 5.412 0 12.046c0 2.123.555 4.195 1.608 6.014L0 24l6.117-1.605a11.782 11.782 0 005.925 1.587h.005c6.632 0 12.043-5.414 12.047-12.048a11.821 11.821 0 00-3.526-8.513"/>
        </svg>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;


